import { Card, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import IUser from "@/interfaces/user.interface";
import { UsersFiltersMessages } from "@/messages/users/users-filters.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { UserFilterKey } from "../../types";

type Props = {
  messages: UsersFiltersMessages;
};

const GRID_SIZES = {
  LARGE: { lg: 4, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const USER_STATUS = {
  ACTIVE: "A",
  WAITING_FOR_CONFIRMATION: "W",
  INACTIVE: "I",
} as const;

const FILTER_CONSTANTS = {
  NOT_FOUND_INDEX: -1,
  EMPTY_STRING: "",
} as const;

function UserFiltersForm({ messages }: Props) {
  const { values, setValues, submitForm, errors } = useFormikContext<IFilter<IUser>[]>();

  const getValueFromProperty = (
    property: UserFilterKey,
    values: IFilter<IUser>[]
  ): IFilter<IUser> | undefined => {
    return values.find((f) => f.property === property);
  };

  const handleChange = (
    property: UserFilterKey,
    value: string | number | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const filters = [...prevFilters];
      const existingIndex = filters.findIndex((f) => f.property === property);

      if (existingIndex !== FILTER_CONSTANTS.NOT_FOUND_INDEX) {
        filters.splice(existingIndex, 1);
      }

      const isEmpty =
        value === null ||
        value === FILTER_CONSTANTS.EMPTY_STRING ||
        (typeof value === "number" && isNaN(value));
      if (isEmpty) {
        return filters;
      }

      const newFilter: IFilter<IUser> = {
        property,
        filterType: filterType,
        value: value,
      };

      return [...filters, newFilter];
    });
  };

  const getStatusOptions = () => [
    { value: USER_STATUS.ACTIVE, label: messages.statusActiveLabel },
    {
      value: USER_STATUS.WAITING_FOR_CONFIRMATION,
      label: messages.statusWaitingForConfirmationLabel,
    },
    { value: USER_STATUS.INACTIVE, label: messages.statusInactiveLabel },
  ];

  const renderTextField = (
    name: string,
    label: string,
    property: UserFilterKey,
    filterType: FilterType = FilterType.Contains
  ) => (
    <TextField
      name={name}
      label={label}
      onChange={(e) => handleChange(property, e.target.value, filterType)}
      value={getValueFromProperty(property, values)?.value ?? FILTER_CONSTANTS.EMPTY_STRING}
      error={!!errors[0] && !!errors[0].value}
      helperText={!!errors[0] && errors[0].value}
      fullWidth
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );

  const renderStatusSelect = () => (
    <TextField
      name="status"
      label={messages.statusLabel}
      select
      fullWidth
      variant="outlined"
      value={getValueFromProperty("status", values)?.value ?? FILTER_CONSTANTS.EMPTY_STRING}
      onChange={(e) => handleChange("status", e.target.value, FilterType.Equals)}
      error={!!errors[0]?.value}
      helperText={errors[0]?.value}
      slotProps={{
        inputLabel: { shrink: true },
      }}
    >
      {getStatusOptions().map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.LARGE}>
            {renderTextField("email", messages.emailLabel, "email")}
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>{renderTextField("name", messages.nameLabel, "name")}</Grid>
          <Grid size={GRID_SIZES.LARGE}>{renderStatusSelect()}</Grid>

          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={() => setValues([])} fullWidth>
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default UserFiltersForm;
